import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from '../../../../store';
import { TeamService } from '../../../../services/team.service';
import { GroupService } from '../../../../services/groups.service';
import { UnifiedFilterBar, FilterField } from '../../../../components/shared/UnifiedFilterBar';
import { permissionCheck } from '../../../../helpers/permissionCheck';
import { customerPermissionFlags } from '../../../../constants/systemPermissions.constants';
import { TEST_MODE_COPY } from '../testMode.copy';
import { EMPTY_FILTER_VALUES, TestModeFilterValues } from '../testMode.types';

const COPY = TEST_MODE_COPY.filters;

interface Props {
    onApply: (applied: TestModeFilterValues) => void;
    onExport: () => void | Promise<void>;
}

/**
 * Filter bar for the Test Mode roster, built on the shared UnifiedFilterBar
 * (staged edits, Apply commits — matching the app pattern and the prototype).
 *
 * Primary row: Status / App Test / Device / User Name.
 * Expanded row: Group + Team. The Group filter is hidden for Team Leaders
 * (their scope is their teams; server enforces it regardless).
 */
export const TestModeFilters: React.FC<Props> = ({ onApply, onExport }) => {
    const [values, setValues] = useState<TestModeFilterValues>(EMPTY_FILTER_VALUES);
    const [applied, setApplied] = useState<TestModeFilterValues>(EMPTY_FILTER_VALUES);

    const { items: allTeams } = useSelector((state: RootState) => state.teams);
    const { items: allGroups } = useSelector((state: RootState) => state.groups);

    useEffect(() => {
        TeamService.fetchAll(() => {});
        GroupService.fetchAll(() => {});
    }, []);

    const canFilterByGroup = permissionCheck(customerPermissionFlags.GROUP_LEADER);

    // Narrow the team list to the chosen group, and section the full list by
    // group name otherwise (same behavior as the alerts filter bar).
    const groupNameById = useMemo(() => {
        const m = new Map<string, string>();
        (allGroups ?? []).forEach((g: any) => m.set(g.id || g._id || '', g.name || ''));
        return m;
    }, [allGroups]);

    const teamOptions = useMemo(() => {
        let teams = allTeams ?? [];
        if (values.groupId) teams = teams.filter((t: any) => t.groupId === values.groupId);
        return teams.map((t: any) => ({
            label: t.name,
            value: t.id,
            group: values.groupId ? undefined : groupNameById.get(t.groupId ?? '') || undefined,
        }));
    }, [allTeams, values.groupId, groupNameById]);

    const groupOptions = useMemo(
        () => (allGroups ?? []).map((g: any) => ({ label: g.name, value: g.id || g._id })),
        [allGroups],
    );

    const primaryFilters: FilterField[] = useMemo(
        () => [
            { key: 'status', label: COPY.status.label, type: 'select', options: [...COPY.status.options] },
            { key: 'appTest', label: COPY.appTest.label, type: 'select', options: [...COPY.appTest.options] },
            { key: 'device', label: COPY.device.label, type: 'select', options: [...COPY.device.options] },
            { key: 'name', label: COPY.name.label, type: 'text', placeholder: COPY.name.placeholder },
        ],
        [],
    );

    const expandedFilters: FilterField[] = useMemo(
        () => [
            ...(canFilterByGroup
                ? [{ key: 'groupId', label: COPY.group.label, type: 'select' as const, options: groupOptions }]
                : []),
            { key: 'teamId', label: COPY.team.label, type: 'select', options: teamOptions },
        ],
        [canFilterByGroup, groupOptions, teamOptions],
    );

    const handleChange = (key: string, value: any) => {
        setValues((prev) => {
            const next = { ...prev, [key]: value };
            // A group change invalidates a team that belongs to another group.
            if (key === 'groupId' && prev.teamId) {
                const stillValid = (allTeams ?? []).some(
                    (t: any) => t.id === prev.teamId && (!value || t.groupId === value),
                );
                if (!stillValid) next.teamId = '';
            }
            return next;
        });
    };

    const handleApply = () => {
        setApplied(values);
        onApply(values);
    };

    const handleClear = () => {
        setValues(EMPTY_FILTER_VALUES);
        setApplied(EMPTY_FILTER_VALUES);
        onApply(EMPTY_FILTER_VALUES);
    };

    const removeApplied = (key: keyof TestModeFilterValues) => {
        const next = { ...applied, [key]: '' };
        setApplied(next);
        setValues((prev) => ({ ...prev, [key]: '' }));
        onApply(next);
    };

    const pillLabel = (key: keyof TestModeFilterValues, value: string): string => {
        const optionLabel = (opts: readonly { label: string; value: string }[]) =>
            opts.find((o) => o.value === value)?.label ?? value;
        switch (key) {
            case 'status': return `${COPY.status.label}: ${optionLabel(COPY.status.options)}`;
            case 'appTest': return `${COPY.appTest.label}: ${optionLabel(COPY.appTest.options)}`;
            case 'device': return `${COPY.device.label}: ${optionLabel(COPY.device.options)}`;
            case 'name': return `${COPY.name.label}: ${value}`;
            case 'groupId': return `${COPY.group.label}: ${groupOptions.find((o) => o.value === value)?.label ?? value}`;
            case 'teamId': return `${COPY.team.label}: ${(allTeams ?? []).find((t: any) => t.id === value)?.name ?? value}`;
            default: return value;
        }
    };

    const appliedPills = (Object.keys(applied) as Array<keyof TestModeFilterValues>)
        .filter((key) => applied[key] !== '')
        .map((key) => ({
            key,
            label: pillLabel(key, applied[key]),
            onRemove: () => removeApplied(key),
        }));

    return (
        <UnifiedFilterBar
            primaryFilters={primaryFilters}
            expandedFilters={expandedFilters}
            values={values}
            onChange={handleChange}
            onApply={handleApply}
            onClear={handleClear}
            onExport={onExport}
            appliedFilters={appliedPills}
        />
    );
};
